import { useState, useEffect } from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  BanknotesIcon,
  CheckCircleIcon,
  XCircleIcon,
  CreditCardIcon,
  BuildingLibraryIcon
} from '@heroicons/react/24/outline';

const ContasBancarias = () => {
  const [contas, setContas] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [carregando, setCarregando] = useState(true);

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      tipo: 'corrente',
      ativa: true,
      saldo_inicial: 0,
      codigo_contabil_integracao: ''
    }
  });

  useEffect(() => {
    carregarContas();
  }, []);

  const toNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const carregarContas = async () => {
    setCarregando(true);
    try {
      const response = await axios.get('/api/contas-bancarias');
      setContas(
        (response.data || []).map((conta) => ({
          ...conta,
          saldo_inicial: toNumber(conta.saldo_inicial),
          saldo_atual: toNumber(conta.saldo_atual),
        }))
      );
    } catch (error) {
      console.error('Erro ao carregar contas:', error);
      toast.error('Erro ao carregar contas bancárias');
    } finally {
      setCarregando(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      if (editingId) {
        await axios.put(`/api/contas-bancarias/${editingId}`, data);
        toast.success('Conta atualizada com sucesso!');
      } else {
        await axios.post('/api/contas-bancarias', data);
        toast.success('Conta cadastrada com sucesso!');
      }
      
      setShowModal(false);
      reset();
      setEditingId(null);
      carregarContas();
    } catch (error) {
      console.error('Erro ao salvar conta:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar conta');
    }
  };

  const handleEdit = (conta) => {
    reset(conta);
    setEditingId(conta.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta conta bancária?')) {
      return;
    }

    try {
      await axios.delete(`/api/contas-bancarias/${id}`);
      toast.success('Conta excluída com sucesso!');
      carregarContas();
    } catch (error) {
      console.error('Erro ao excluir conta:', error);
      toast.error('Erro ao excluir conta bancária');
    }
  };

  const toggleStatus = async (id, ativa) => {
    try {
      await axios.patch(`/api/contas-bancarias/${id}`, { ativa: !ativa });
      toast.success('Status da conta atualizado!');
      carregarContas();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      toast.error('Erro ao alterar status da conta');
    }
  };

  const saldoTotal = contas.reduce((total, conta) => total + conta.saldo_atual, 0);
  const contasAtivas = contas.filter(c => c.ativa).length;

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">💰 Contas Bancárias</h1>
          <p className="text-gray-600">Gerencie as contas bancárias da empresa</p>
        </div>
        <button
          onClick={() => {
            reset();
            setEditingId(null);
            setShowModal(true);
          }}
          className="btn-primary"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Nova Conta
        </button>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="card bg-primary-50 border-primary-200">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-primary-100">
                <BuildingLibraryIcon className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-primary-600">Total de Contas</p>
                <p className="text-2xl font-bold text-gray-900">{contas.length}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-success-50 border-success-200">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-success-100">
                <BanknotesIcon className="h-6 w-6 text-success-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-success-600">Saldo Total</p>
                <p className="text-2xl font-bold text-gray-900">
                  R$ {saldoTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-warning-50 border-warning-200">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-warning-100">
                <CreditCardIcon className="h-6 w-6 text-warning-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-warning-600">Contas Ativas</p>
                <p className="text-2xl font-bold text-gray-900">{contasAtivas}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Contas */}
      <div className="card">
        <div className="card-header flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            Lista de Contas ({contas.length})
          </h3>
          {carregando && (
            <div className="loader h-6 w-6"></div>
          )}
        </div>
        
        <div className="card-body p-0">
          {contas.length > 0 ? (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Banco</th>
                    <th>Agência/Conta</th>
                    <th>Tipo</th>
                    <th>Conta Contábil</th>
                    <th>Saldo Atual</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {contas.map((conta) => (
                    <tr key={conta.id}>
                      <td>
                        <div className="flex items-center">
                          <BuildingLibraryIcon className="h-5 w-5 text-gray-400 mr-2" />
                          <span className="font-medium">{conta.banco}</span>
                        </div>
                      </td>
                      <td>
                        <div>
                          <p className="font-medium">{conta.agencia} / {conta.conta}</p>
                          <p className="text-xs text-gray-500">ID: {conta.id}</p>
                        </div>
                      </td>
                      <td>
                        <span className="badge-info capitalize">
                          {conta.tipo === 'corrente' ? 'Corrente' : 
                           conta.tipo === 'poupanca' ? 'Poupança' : 'Investimento'}
                        </span>
                      </td>
                      <td>{conta.codigo_contabil_integracao || '-'}</td>
                      <td>
                        <div className={`font-semibold ${
                          conta.saldo_atual >= 0 ? 'text-success-600' : 'text-danger-600'
                        }`}>
                          R$ {conta.saldo_atual.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </div>
                        {conta.saldo_inicial > 0 && (
                          <p className="text-xs text-gray-500">
                            Inicial: R$ {conta.saldo_inicial.toFixed(2)}
                          </p>
                        )}
                      </td>
                      <td>
                        <button
                          onClick={() => toggleStatus(conta.id, conta.ativa)}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            conta.ativa 
                              ? 'bg-success-100 text-success-800 hover:bg-success-200' 
                              : 'bg-danger-100 text-danger-800 hover:bg-danger-200'
                          }`}
                        >
                          {conta.ativa ? (
                            <>
                              <CheckCircleIcon className="h-3 w-3 mr-1" />
                              Ativa
                            </>
                          ) : (
                            <>
                              <XCircleIcon className="h-3 w-3 mr-1" />
                              Inativa
                            </>
                          )}
                        </button>
                      </td>
                      <td>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(conta)}
                            className="text-primary-600 hover:text-primary-900 p-1"
                            title="Editar"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(conta.id)}
                            className="text-danger-600 hover:text-danger-900 p-1"
                            title="Excluir"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <BanknotesIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-lg font-medium">Nenhuma conta bancária cadastrada</p>
              <p className="text-sm mt-1">Cadastre sua primeira conta bancária para começar</p>
              <button
                onClick={() => setShowModal(true)}
                className="btn-primary mt-4"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Cadastrar Primeira Conta
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Conta Bancária */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">
                {editingId ? '✏️ Editar Conta' : '➕ Nova Conta Bancária'}
              </h3>
            </div>
            
            <form onSubmit={handleSubmit(onSubmit)} className="p-6">
              <div className="space-y-4">
                <div className="form-group">
                  <label className="form-label">Banco *</label>
                  <input
                    type="text"
                    {...register('banco', { 
                      required: 'Banco é obrigatório',
                      minLength: {
                        value: 2,
                        message: 'Mínimo 2 caracteres'
                      }
                    })}
                    className="form-input"
                    placeholder="Ex: Itaú, Bradesco, Santander"
                  />
                  {errors.banco && (
                    <p className="form-error">{errors.banco.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">Agência *</label>
                    <input
                      type="text"
                      {...register('agencia', { 
                        required: 'Agência é obrigatória'
                      })}
                      className="form-input"
                      placeholder="0000"
                    />
                    {errors.agencia && (
                      <p className="form-error">{errors.agencia.message}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Conta *</label>
                    <input
                      type="text"
                      {...register('conta', { 
                        required: 'Conta é obrigatória'
                      })}
                      className="form-input"
                      placeholder="00000-0"
                    />
                    {errors.conta && (
                      <p className="form-error">{errors.conta.message}</p>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Tipo *</label>
                  <select
                    {...register('tipo', { required: 'Tipo é obrigatório' })}
                    className="form-select"
                  >
                    <option value="corrente">Conta Corrente</option>
                    <option value="poupanca">Conta Poupança</option>
                    <option value="investimento">Conta Investimento</option>
                  </select>
                  {errors.tipo && (
                    <p className="form-error">{errors.tipo.message}</p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Conta Contábil de Integração</label>
                  <input
                    type="text"
                    {...register('codigo_contabil_integracao')}
                    className="form-input"
                    placeholder="Ex: 1.1.1.01"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Usada como débito/crédito na exportação para o sistema contábil.
                  </p>
                </div>

                <div className="form-group">
                  <label className="form-label">Saldo Inicial (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('saldo_inicial', { 
                      min: {
                        value: 0,
                        message: 'Saldo não pode ser negativo'
                      }
                    })}
                    className="form-input"
                    placeholder="0,00"
                  />
                  {errors.saldo_inicial && (
                    <p className="form-error">{errors.saldo_inicial.message}</p>
                  )}
                </div>

                <div className="form-group">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      {...register('ativa')}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Conta ativa</span>
                  </label>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    reset();
                    setEditingId(null);
                  }}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  {editingId ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContasBancarias;
